// src/components/property-tools-grid/PropertyToolsGrid.tsx

import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { startTransition } from 'react';
import { PropertyTool, propertyTools } from '../../../data/propertyTools';

const PropertyToolsGrid: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    startTransition(() => {
      navigate(path);
    });
  };

  return (
    <>
      <Helmet>
        <title>RentSmart | Rental Yield Calculator & Property Tools Ireland</title>
        <meta
          name="description"
          content="Explore RentSmart's suite of property tools for Irish landlords, renters, and agents. Calculate rent, check RPZ status, estimate home value, and more."
        />
        <meta
          name="keywords"
          content="property tools, rent calculator, RPZ checker, home valuation, Ireland, landlord tools, renter tools, property management"
        />
        <link rel="canonical" href="https://rentsmart.ie/property-tools" />
        <meta property="og:title" content="RentSmart | Rental Yield Calculator & Property Tools Ireland" />
        <meta
          property="og:description"
          content="Discover essential property tools for the Irish market. Instantly calculate rent, check RPZ, and estimate your home's value."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/property-tools" />
      </Helmet>
      <section className="mt-12">
        <h1 className="text-2xl font-semibold mb-6 text-center">
          RentSmart Property Tools: Calculate Rental Yields and Check Leases in Ireland
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* The 'Price Trends by County' card is now data-driven from propertyTools */}
          {propertyTools.map((tool: PropertyTool) => {
            const isFeatured = (tool as any).featured;
            return (
              <div
                key={tool.name}
                className={
                  isFeatured
                    ? 'bg-blue-50 rounded-lg shadow-md p-6 flex flex-col items-center justify-center hover:bg-blue-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer border border-blue-200 mb-8'
                    : 'bg-gray-50 rounded-lg shadow-md p-6 flex flex-col items-start hover:bg-white hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer border border-gray-100 hover:border-blue-200'
                }
                onClick={() => handleNavigation(tool.path)}
                tabIndex={0}
                role="button"
                aria-label={tool.name}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    handleNavigation(tool.path);
                }}
              >
                <tool.icon
                  className={
                    isFeatured
                      ? 'h-8 w-8 text-blue-600 mb-4'
                      : 'h-8 w-8 text-blue-600 mb-4'
                  }
                  aria-hidden="true"
                />
                <h3
                  className={
                    isFeatured
                      ? 'text-lg font-bold text-blue-700 mb-1 text-center'
                      : 'text-lg font-medium mb-2'
                  }
                >
                  {tool.name}
                </h3>
                <p
                  className={
                    isFeatured
                      ? 'text-xs text-blue-500 text-center'
                      : 'text-gray-600 flex-grow'
                  }
                >
                  {tool.description}
                </p>
                {!isFeatured && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigation(tool.path);
                    }}
                    className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold group"
                  >
                    Learn More
                    <svg
                      className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M9 5l7 7-7 7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default PropertyToolsGrid;
